import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';

import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc,
  updateDoc,
  increment,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { UserRole } from '../types/roles';
import { logger } from '../utils/debugLogger';
import { getActivePPBoost, applyPPBoost, type PPBoost } from '../utils/ppBoost';
import { resolveCanonicalPP } from '../utils/playerPowerPoints';
import SearchBar from './SearchBar';
import ClassScorekeeperAssignment from './ClassScorekeeperAssignment';
import { searchStudents } from '../utils/searchUtils';

// FORCE DEBUG LOG - This should appear in console when component loads
console.log('🚨 ScorekeeperInterface.tsx: COMPONENT FILE LOADED - VERSION 2.0');

interface Student {
  id: string;
  displayName: string;
  email: string;
  powerPoints: number;
  photoURL?: string;
  level?: number;
  xp?: number;
  classId?: string;
}


const ScorekeeperInterface: React.FC = () => {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('student');
  const [loading, setLoading] = useState<boolean>(true);
  const [assignedClassId, setAssignedClassId] = useState<string>('');
  const [className, setClassName] = useState<string>('');
  const [ppInputValues, setPPInputValues] = useState<Record<string, number | undefined>>({});
  const [pendingChanges, setPendingChanges] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeBoosts, setActiveBoosts] = useState<Record<string, PPBoost | null>>({});
  const [availableClassrooms, setAvailableClassrooms] = useState<Array<{ id: string; name: string }>>([]);
  
  // Sorting state
  const [sortMode, setSortMode] = useState<'firstName' | 'lastName' | 'pp'>('firstName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  
  // Bulk selection state
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [bulkPPAmount, setBulkPPAmount] = useState<number>(0);
  const [bulkReason, setBulkReason] = useState<string>('');
  const [isApplyingBulk, setIsApplyingBulk] = useState<boolean>(false);

  // Check if current user is a scorekeeper and get assigned class
  useEffect(() => {
    const checkUserRole = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        logger.roles.debug('ScorekeeperInterface: Checking user role for:', currentUser.uid);
        const roleDoc = await getDoc(doc(db, 'userRoles', currentUser.uid));
        
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          const detectedRole = roleData.role || 'student';
          const classId = roleData.classId; // Legacy field
          const classIds = roleData.classIds || []; // New multi-class field
          
          // Check if user has scorekeeper or admin role in the roles array
          const hasScorekeeperRole = roleData.roles && Array.isArray(roleData.roles) && roleData.roles.includes('scorekeeper');
          const hasAdminRole = roleData.roles && Array.isArray(roleData.roles) && roleData.roles.includes('admin');
          const finalRole = detectedRole === 'scorekeeper' || hasScorekeeperRole ? 'scorekeeper' : 
                           detectedRole === 'admin' || hasAdminRole ? 'admin' : detectedRole;
          
          logger.roles.info('ScorekeeperInterface: User role detected:', { 
            userId: currentUser.uid, 
            email: currentUser.email,
            role: detectedRole,
            roles: roleData.roles,
            hasScorekeeperRole,
            hasAdminRole,
            finalRole,
            classId: classId,
            classIds: classIds
          });
          
          setUserRole(finalRole);
          
          // Handle multi-class scorekeeper assignments
          if (finalRole === 'scorekeeper') {
            const scorekeeperClassIds = Array.from(
              new Set(
                [...(Array.isArray(classIds) ? classIds : []), ...(classId ? [classId] : [])].filter(
                  (id): id is string => typeof id === 'string' && id.length > 0
                )
              )
            );

            if (scorekeeperClassIds.length > 0) {
              // Deleted classrooms stay listed on userRoles; defaulting to one shows an empty roster.
              const classOptions: Array<{ id: string; name: string }> = [];
              const unreadable: Array<{ id: string; name: string }> = [];
              for (const cid of scorekeeperClassIds) {
                try {
                  const classDoc = await getDoc(doc(db, 'classrooms', cid));
                  if (classDoc.exists()) {
                    const classData = classDoc.data();
                    classOptions.push({
                      id: cid,
                      name: classData.name || `Class ${cid}`,
                    });
                  } else {
                    logger.roles.warn('ScorekeeperInterface: Assigned classroom no longer exists', { classId: cid });
                  }
                } catch (classError) {
                  logger.roles.warn('ScorekeeperInterface: Could not read assigned classroom', { classId: cid, classError });
                  unreadable.push({ id: cid, name: `Class ${cid}` });
                }
              }
              const options = classOptions.length > 0 ? classOptions : unreadable;
              setAvailableClassrooms(options);
              if (options.length > 0) {
                setAssignedClassId(options[0].id);
                setClassName(options[0].name);
              }
            }
          } else if (finalRole === 'admin') {
            // For admins, load all classrooms
            try {
              const classroomsSnapshot = await getDocs(collection(db, 'classrooms'));
              const classrooms = classroomsSnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name || `Class ${doc.id}`
              }));
              setAvailableClassrooms(classrooms);
              
              // Set default to first classroom or 'admin-all-classes'
              if (classrooms.length > 0) {
                setAssignedClassId(classrooms[0].id);
                setClassName(classrooms[0].name);
              } else {
                setAssignedClassId('admin-all-classes');
                setClassName('All Classes (Admin Access)');
              }
            } catch (error) {
              logger.roles.error('ScorekeeperInterface: Error loading classrooms for admin:', error);
              setAssignedClassId('admin-all-classes');
              setClassName('All Classes (Admin Access)');
            }
          }
          
          // Also load classrooms if user is admin by email (even if role doc says otherwise)
          const isAdminByEmailCheck = currentUser?.email === 'eddymosley@compscihigh.org' || 
                                     currentUser?.email === 'admin@mstgames.net' ||
                                     currentUser?.email === 'edm21179@gmail.com' ||
                                     currentUser?.email === 'eddymosley9@gmail.com' ||
                                     currentUser?.email?.includes('eddymosley') ||
                                     currentUser?.email?.includes('admin') ||
                                     currentUser?.email?.includes('mstgames');
          
          if (isAdminByEmailCheck && availableClassrooms.length === 0) {
            // Load classrooms for email-based admins
            try {
              const classroomsSnapshot = await getDocs(collection(db, 'classrooms'));
              const classrooms = classroomsSnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name || `Class ${doc.id}`
              }));
              setAvailableClassrooms(classrooms);
              
              // Set default to first classroom or 'admin-all-classes'
              if (classrooms.length > 0 && !assignedClassId) {
                setAssignedClassId(classrooms[0].id);
                setClassName(classrooms[0].name);
              } else if (!assignedClassId) {
                setAssignedClassId('admin-all-classes');
                setClassName('All Classes (Admin Access)');
              }
            } catch (error) {
              logger.roles.error('ScorekeeperInterface: Error loading classrooms for email-based admin:', error);
              if (!assignedClassId) {
                setAssignedClassId('admin-all-classes');
                setClassName('All Classes (Admin Access)');
              }
            }
          }
        } else {
          logger.roles.warn('ScorekeeperInterface: No role document found for user:', currentUser.uid);
          setUserRole('student');
        }
      } catch (error) {
        logger.roles.error('ScorekeeperInterface: Error checking user role:', error);
        
        // TEMPORARY WORKAROUND: If we get permission error but NavBar shows admin/scorekeeper,
        // assume user is admin (this handles the permission timing issue)
        if (error instanceof Error && error.message?.includes('Missing or insufficient permissions')) {
          logger.roles.warn('ScorekeeperInterface: Permission error, but NavBar shows admin role - assuming admin');
          setUserRole('admin');
          setClassName('All Classes (Admin Access)');
        } else {
        setUserRole('student');
        }
      } finally {
        setLoading(false);
      }
    };

    checkUserRole();
  }, [currentUser]);

  // Load classrooms for email-based admins if not already loaded
  useEffect(() => {
    const loadClassroomsForEmailAdmin = async () => {
      if (!currentUser) return;
      
      const isAdminByEmailCheck = currentUser?.email === 'eddymosley@compscihigh.org' || 
                                   currentUser?.email === 'admin@mstgames.net' ||
                                   currentUser?.email === 'edm21179@gmail.com' ||
                                   currentUser?.email === 'eddymosley9@gmail.com' ||
                                   currentUser?.email?.includes('eddymosley') ||
                                   currentUser?.email?.includes('admin') ||
                                   currentUser?.email?.includes('mstgames');
      
      if (isAdminByEmailCheck && availableClassrooms.length === 0) {
        try {
          const classroomsSnapshot = await getDocs(collection(db, 'classrooms'));
          const classrooms = classroomsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name || `Class ${doc.id}`
          }));
          setAvailableClassrooms(classrooms);
          
          // Set default to first classroom if not already set
          if (classrooms.length > 0 && (!assignedClassId || assignedClassId === 'admin-all-classes')) {
            setAssignedClassId(classrooms[0].id);
            setClassName(classrooms[0].name);
          }
        } catch (error) {
          console.error('Error loading classrooms for email-based admin:', error);
        }
      }
    };
    
    loadClassroomsForEmailAdmin();
  }, [currentUser, availableClassrooms.length, assignedClassId]);

  // Load students for the assigned class
  useEffect(() => {
    const loadStudents = async () => {
      if (
        (userRole !== 'scorekeeper' && userRole !== 'admin') ||
        !assignedClassId
      ) {
        setStudents([]);
        setFilteredStudents([]);
        return;
      }
      
      try {
        logger.roster.debug('ScorekeeperInterface: Loading students for class:', assignedClassId);
        
        // Use the same approach as AdminPanel - load from both collections
        const studentsSnapshot = await getDocs(collection(db, 'students'));
        let userDocs: Array<{ id: string; data: () => Record<string, any> }> = [];
        try {
          userDocs = (await getDocs(collection(db, 'users'))).docs;
        } catch (usersError) {
          logger.roster.warn('ScorekeeperInterface: Could not read users; using students data only', usersError);
        }
        
        logger.roster.info('ScorekeeperInterface: Loaded collections:', {
          students: studentsSnapshot.docs.length,
          users: userDocs.length
        });
        
        // Create a map of user data from the 'users' collection
        const usersMap = new Map();
        userDocs.forEach(doc => {
          const userData = doc.data();
          usersMap.set(doc.id, {
          id: doc.id,
            displayName: userData.displayName || 'Unnamed Student',
            email: userData.email || 'No email',
            photoURL: userData.photoURL,
            createdAt: userData.createdAt,
            lastLogin: userData.lastLogin,
            ...userData
          });
        });
        
        // Merge with student data from 'students' collection
        const allStudents: Student[] = [];
        studentsSnapshot.docs.forEach(doc => {
          const studentData = doc.data();
          const userId = doc.id;
          const userData = usersMap.get(userId) || {};
          
          // Merge user and student data
          const mergedStudent: Student = {
            id: userId,
            displayName: userData.displayName || studentData.displayName || 'Unnamed Student',
            email: userData.email || studentData.email || 'No email',
            photoURL: userData.photoURL || studentData.photoURL,
            powerPoints: studentData.powerPoints || 0,
            level: studentData.level || 1,
            xp: studentData.xp || 0,
            classId: studentData.classId || '',
            ...userData,
            ...studentData
          };
          
          allStudents.push(mergedStudent);
        });

        // Filter to the selected classroom roster only (never fail-open to every student).
        let finalStudents: Student[] = [];
        if (!assignedClassId) {
          logger.roster.warn('ScorekeeperInterface: No assignedClassId — showing empty list');
          finalStudents = [];
        } else if (assignedClassId === 'admin-all-classes') {
          logger.roster.info('ScorekeeperInterface: Admin access - showing all students:', {
            totalStudents: allStudents.length
          });
          finalStudents = allStudents;
        } else {
          try {
            const classDoc = await getDoc(doc(db, 'classrooms', assignedClassId));
            if (!classDoc.exists()) {
              logger.roster.warn(
                'ScorekeeperInterface: Classroom not found — showing empty list (not all students)',
                { classId: assignedClassId }
              );
              finalStudents = [];
            } else {
              const classData = classDoc.data();
              const rawRoster = classData.students;
              const classStudentIds = Array.isArray(rawRoster)
                ? rawRoster.filter((id): id is string => typeof id === 'string' && id.length > 0)
                : [];

              // Prefer classroom.students roster; also include students whose classId matches
              // (recovery when roster is stale) — still scoped to this class only.
              const rosterSet = new Set(classStudentIds);
              const classStudents = allStudents.filter(
                (student) =>
                  rosterSet.has(student.id) ||
                  (typeof student.classId === 'string' && student.classId === assignedClassId)
              );

              logger.roster.info('ScorekeeperInterface: Loaded students:', {
                totalStudents: allStudents.length,
                classStudents: classStudents.length,
                classId: assignedClassId,
                rosterCount: classStudentIds.length,
              });

              finalStudents = classStudents;
            }
          } catch (classError) {
            logger.roster.warn(
              'ScorekeeperInterface: Classroom access failed — showing empty list (not all students):',
              classError
            );
            finalStudents = [];
          }
        }
        
        // Show the canonical balance (vault) so scorekeepers see the same PP as players.
        const canonicalStudents = await Promise.all(
          finalStudents.map(async (student) => {
            try {
              const vaultSnap = await getDoc(doc(db, 'vaults', student.id));
              const powerPoints = resolveCanonicalPP({
                vaultExists: vaultSnap.exists(),
                vaultPP: vaultSnap.exists() ? vaultSnap.data()?.currentPP : undefined,
                studentPP: student.powerPoints,
              });
              return { ...student, powerPoints };
            } catch {
              return student;
            }
          })
        );

        setStudents(canonicalStudents);
        setFilteredStudents(canonicalStudents);
        
        const boosts: Record<string, PPBoost | null> = {};
        await Promise.all(
          canonicalStudents.map(async (student) => {
            try {
              boosts[student.id] = await getActivePPBoost(student.id);
            } catch (error) {
              console.error(`Error checking PP boost for student ${student.id}:`, error);
              boosts[student.id] = null;
            }
          })
        );
        setActiveBoosts(boosts);
      } catch (error) {
        logger.roster.error('ScorekeeperInterface: Error loading students:', error);
      }
    };

    loadStudents();
  }, [assignedClassId, userRole]);

  // Filter students based on search query
  useEffect(() => {
    const filtered = searchStudents(students, searchQuery);
    setFilteredStudents(filtered);
  }, [students, searchQuery]);

  // Search handlers
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Track original (unboosted) changes separately from displayed (boosted) changes
  const [originalPendingChanges, setOriginalPendingChanges] = useState<{ [studentId: string]: number }>({});

  // Handle PP adjustment (now tracks pending changes)
  // students[].powerPoints is always the saved balance; pending changes are layered on top for display.
  const boostedAmount = (studentId: string, change: number) =>
    change > 0 ? applyPPBoost(change, studentId, activeBoosts[studentId] ?? null) : change;

  const displayPP = (student: Student) =>
    Math.max(0, (student.powerPoints || 0) + (pendingChanges[student.id] || 0));

  const handleAdjustPP = (studentId: string, change: number) => {
    if (!students.some(s => s.id === studentId) || !change) return;
    const boostedChange = boostedAmount(studentId, change);
    setOriginalPendingChanges(prev => ({ ...prev, [studentId]: (prev[studentId] || 0) + change }));
    setPendingChanges(prev => ({ ...prev, [studentId]: (prev[studentId] || 0) + boostedChange }));
  };

  // Submit pending changes for admin approval
  const handleSubmitChanges = async () => {
    if (!currentUser || !assignedClassId) return;

    // Use original (unboosted) changes for submission
    const changesToSubmit = Object.entries(originalPendingChanges).filter(([_, change]) => change !== 0);
    if (changesToSubmit.length === 0) {
      alert('No changes to submit');
      return;
    }

    setIsSubmitting(true);
    try {
      // Create a change request document
      const changeRequest = {
        scorekeeperId: currentUser.uid,
        scorekeeperEmail: currentUser.email,
        classId: assignedClassId,
        className: className,
        changes: changesToSubmit.map(([studentId, originalChange]) => {
          const student = students.find(s => s.id === studentId);
          const currentPP = student?.powerPoints || 0;
          
          return {
            studentId,
            studentName: student?.displayName || 'Unknown',
            studentEmail: student?.email || '',
            currentPP,
            changeAmount: originalChange, // Store original (unboosted) amount
            newPP: Math.max(0, currentPP + (pendingChanges[studentId] || 0))
          };
        }),
        submittedAt: serverTimestamp(),
        status: 'pending',
        reviewedBy: null,
        reviewedAt: null
      };

      await addDoc(collection(db, 'ppChangeRequests'), changeRequest);

      // Create notification for admin
      try {
        await addDoc(collection(db, 'adminNotifications'), {
          type: 'pp_change_submitted',
          message: `${currentUser.email} submitted ${changesToSubmit.length} PP changes for ${className} requiring approval.`,
          scorekeeperId: currentUser.uid,
          scorekeeperEmail: currentUser.email,
          className: className,
          changesCount: changesToSubmit.length,
          timestamp: serverTimestamp(),
          read: false
        });
      } catch (notificationError) {
        console.error('Error creating admin notification:', notificationError);
      }

      logger.roster.info('ScorekeeperInterface: Submitted changes for approval:', {
        scorekeeperId: currentUser.uid,
        classId: assignedClassId,
        changesCount: changesToSubmit.length
      });

      // Clear pending changes
      setPendingChanges({});
      setOriginalPendingChanges({});

      alert(`Successfully submitted ${changesToSubmit.length} changes for admin approval!`);
    } catch (error) {
      logger.roster.error('ScorekeeperInterface: Error submitting changes:', error);
      alert('Error submitting changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bulk PP adjustment function (tracks pending changes instead of applying directly)
  const handleBulkAdjustPP = async () => {
    if (!currentUser || selectedStudents.size === 0 || bulkPPAmount === 0 || isApplyingBulk) {
      return;
    }

    setIsApplyingBulk(true);
    try {
      const originalChangeAmount = bulkPPAmount;
      const selectedIds = Array.from(selectedStudents).filter(id => students.some(s => s.id === id));
      const boostedById: Record<string, number> = {};
      selectedIds.forEach(id => {
        boostedById[id] = boostedAmount(id, originalChangeAmount);
      });

      setOriginalPendingChanges(prev => {
        const next = { ...prev };
        selectedIds.forEach(id => {
          next[id] = (next[id] || 0) + originalChangeAmount;
        });
        return next;
      });
      setPendingChanges(prev => {
        const next = { ...prev };
        selectedIds.forEach(id => {
          next[id] = (next[id] || 0) + boostedById[id];
        });
        return next;
      });
      const updatedStudents = selectedIds;

      logger.roster.info('ScorekeeperInterface: Bulk PP changes tracked for approval:', {
        studentCount: updatedStudents.length,
        changeAmount: originalChangeAmount,
        bulkReason
      });

      // Store values for alert before clearing
      const action = originalChangeAmount > 0 ? 'added' : 'subtracted';
      const absAmount = Math.abs(originalChangeAmount);

      // Clear selection and form
      setSelectedStudents(new Set());
      setBulkPPAmount(0);
      setBulkReason('');

      // Show success message
      alert(`Successfully tracked ${absAmount} PP ${action} for ${updatedStudents.length} student${updatedStudents.length !== 1 ? 's' : ''}! Changes are pending and will be applied after approval.`);
    } catch (error: any) {
      console.error('Error in bulk PP adjustment:', error);
      alert(`Failed to track bulk PP adjustment: ${error?.message || error}`);
    } finally {
      setIsApplyingBulk(false);
    }
  };

  // Get count of pending changes
  const pendingChangesCount = Object.values(pendingChanges).filter(change => change !== 0).length;

  // Helper function to extract first and last names from displayName
  const getNameParts = (displayName: string): { firstName: string; lastName: string } => {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ')
    };
  };

  // Sort filtered students based on sortMode and sortDir (must be before early returns for hooks rules)
  const sortedStudents = useMemo(() => {
    const sorted = [...filteredStudents];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      if (sortMode === 'pp') {
        const ppA = a.powerPoints || 0;
        const ppB = b.powerPoints || 0;
        comparison = ppA - ppB;
      } else if (sortMode === 'firstName') {
        const nameA = getNameParts(a.displayName);
        const nameB = getNameParts(b.displayName);
        comparison = nameA.firstName.localeCompare(nameB.firstName, undefined, { sensitivity: 'base' });
        // If first names are equal, compare by last name
        if (comparison === 0) {
          comparison = nameA.lastName.localeCompare(nameB.lastName, undefined, { sensitivity: 'base' });
        }
      } else if (sortMode === 'lastName') {
        const nameA = getNameParts(a.displayName);
        const nameB = getNameParts(b.displayName);
        comparison = nameA.lastName.localeCompare(nameB.lastName, undefined, { sensitivity: 'base' });
        // If last names are equal, compare by first name
        if (comparison === 0) {
          comparison = nameA.firstName.localeCompare(nameB.firstName, undefined, { sensitivity: 'base' });
        }
      }
      
      return sortDir === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [filteredStudents, sortMode, sortDir]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.2rem',
        color: '#6b7280'
      }}>
        Loading Scorekeeper Interface...
      </div>
    );
  }

  // Check if current user is admin - Only Yondaime has access
  const isAdminByEmail = currentUser?.email === 'edm21179@gmail.com';

  // Debug logging for access control
  console.log('🔍 ScorekeeperInterface Access Check:', {
    userRole,
    currentUser: currentUser?.email,
    isAdminByEmail,
    finalAccess: userRole === 'scorekeeper' || userRole === 'admin' || isAdminByEmail
  });

  // FORCE ADMIN ACCESS FOR DEBUGGING - TEMPORARY
  if (isAdminByEmail) {
    console.log('✅ FORCING ADMIN ACCESS - Email-based admin detected');
    // Override userRole to admin for admin emails
    if (userRole !== 'admin') {
      console.log('🔄 Overriding userRole from', userRole, 'to admin');
    }
  }

  if (userRole !== 'scorekeeper' && userRole !== 'admin' && !isAdminByEmail) {
    console.log('❌ Access denied - userRole:', userRole, 'currentUser:', currentUser?.email);
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem' }}>🚫</div>
        <h2 style={{ color: '#ef4444', margin: 0 }}>Access Denied</h2>
        <p style={{ color: '#6b7280', textAlign: 'center', maxWidth: '400px' }}>
          You don't have permission to access the Scorekeeper interface. 
          Contact an administrator if you believe this is an error.
        </p>
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '1rem' }}>
          Debug: userRole = {userRole}, email = {currentUser?.email}
        </div>
      </div>
    );
  }

  if (!assignedClassId && userRole !== 'admin') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem' }}>🏫</div>
        <h2 style={{ color: '#f59e0b', margin: 0 }}>No Class Assigned</h2>
        <p style={{ color: '#6b7280', textAlign: 'center', maxWidth: '400px' }}>
          You aren't assigned to a current class (an old class may have been removed). Contact an administrator to get assigned to a class.
        </p>
      </div>
    );
  }

  const totalPP = students.reduce((sum, student) => sum + displayPP(student), 0);
  const averagePP = students.length > 0 ? Math.round(totalPP / students.length) : 0;

  return (
    <div style={{ 
      padding: '2rem', 
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          fontSize: '2rem', 
          fontWeight: 'bold', 
          margin: 0, 
          color: 'var(--mst-gold-bright)',
          marginBottom: '0.5rem',
          fontFamily: 'var(--mst-font-display)',
          letterSpacing: '0.04em',
        }}>
          ⚡ Class Power Points Overview
        </h1>
        {/* Class dropdown — admins see all classrooms; multi-class scorekeepers see their assigned classes */}
        {(userRole === 'admin' || isAdminByEmail) ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            flexWrap: 'wrap',
            marginBottom: '0.5rem'
          }}>
            <select
              value={assignedClassId || 'admin-all-classes'}
              onChange={(e) => {
                const selectedClassId = e.target.value;
                setAssignedClassId(selectedClassId);
                const selectedClass = availableClassrooms.find(c => c.id === selectedClassId);
                setClassName(selectedClass ? selectedClass.name : 'All Classes (Admin Access)');
                // Clear pending changes when switching classes
                setPendingChanges({});
                setOriginalPendingChanges({});
                setPPInputValues({});
              }}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '1rem',
                border: '2px solid #3b82f6',
                borderRadius: '0.375rem',
                backgroundColor: 'white',
                color: '#1f2937',
                cursor: 'pointer',
                minWidth: '250px',
                fontWeight: '500',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <option value="admin-all-classes">All Classes</option>
              {availableClassrooms.map((classroom) => (
                <option key={classroom.id} value={classroom.id}>
                  {classroom.name}
                </option>
              ))}
            </select>
            <span style={{ 
              fontSize: '1rem', 
              color: 'var(--mst-text-secondary)' 
            }}>
              - Manage Power Points for this class only
            </span>
          </div>
        ) : availableClassrooms.length > 1 ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            flexWrap: 'wrap',
            marginBottom: '0.5rem'
          }}>
            <select
              value={assignedClassId}
              onChange={(e) => {
                const selectedClassId = e.target.value;
                setAssignedClassId(selectedClassId);
                const selectedClass = availableClassrooms.find(c => c.id === selectedClassId);
                setClassName(selectedClass ? selectedClass.name : selectedClassId);
                setPendingChanges({});
                setOriginalPendingChanges({});
                setPPInputValues({});
              }}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '1rem',
                border: '2px solid #3b82f6',
                borderRadius: '0.375rem',
                backgroundColor: 'white',
                color: '#1f2937',
                cursor: 'pointer',
                minWidth: '250px',
                fontWeight: '500',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              {availableClassrooms.map((classroom) => (
                <option key={classroom.id} value={classroom.id}>
                  {classroom.name}
                </option>
              ))}
            </select>
            <span style={{ fontSize: '1rem', color: 'var(--mst-text-secondary)' }}>
              - Students from the selected class only
            </span>
          </div>
        ) : (
          <p style={{ 
            fontSize: '1rem', 
            color: '#6b7280', 
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            {className} - Manage Power Points for this class only
          </p>
        )}
      </div>

      {/* Admin: assign a player as this class's scorekeeper */}
      {(userRole === 'admin' || isAdminByEmail) && currentUser && (
        <ClassScorekeeperAssignment
          classId={assignedClassId || 'admin-all-classes'}
          className={className}
          adminUserId={currentUser.uid}
        />
      )}

      {/* Search Bar */}
      <div style={{ marginBottom: '2rem' }}>
        <SearchBar
          placeholder="Search students by name or email..."
          onSearch={handleSearch}
          onClear={handleClearSearch}
          style={{ maxWidth: '500px' }}
        />
        {searchQuery && (
          <div style={{ 
            marginTop: '0.5rem', 
            fontSize: '0.875rem', 
            color: '#6b7280' 
          }}>
            Showing {filteredStudents.length} of {students.length} students
          </div>
        )}
      </div>

      {/* Sorting Controls */}
      <div style={{ 
        marginBottom: '1.5rem', 
        display: 'flex', 
        gap: '1rem', 
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--mst-text-secondary)' }}>
          Sort by:
        </label>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as 'firstName' | 'lastName' | 'pp')}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
        >
          <option value="firstName">First Name</option>
          <option value="lastName">Last Name</option>
          <option value="pp">PP Amount</option>
        </select>
        <button
          onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}
          title={sortDir === 'asc' ? 'Ascending (A→Z, Low→High)' : 'Descending (Z→A, High→Low)'}
        >
          {sortDir === 'asc' ? '↑' : '↓'} {sortDir === 'asc' ? 'Ascending' : 'Descending'}
        </button>
      </div>

      {/* Instructions Section */}
      <div style={{
        backgroundColor: '#f0f9ff',
        border: '1px solid #0ea5e9',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h2 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          color: '#0c4a6e',
          margin: 0,
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          📋 Scorekeeper Instructions
        </h2>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0.75rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            fontSize: '0.95rem',
            color: '#0c4a6e'
          }}>
            <span style={{ 
              backgroundColor: '#10b981', 
              color: 'white', 
              borderRadius: '50%', 
              width: '24px', 
              height: '24px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '0.875rem',
              fontWeight: 'bold'
            }}>+</span>
            <span><strong>Add points</strong> each time a student earns them</span>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            fontSize: '0.95rem',
            color: '#0c4a6e'
          }}>
            <span style={{ 
              backgroundColor: '#ef4444', 
              color: 'white', 
              borderRadius: '50%', 
              width: '24px', 
              height: '24px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '0.875rem',
              fontWeight: 'bold'
            }}>-</span>
            <span><strong>Subtract points</strong> as directed (Add a '-' sign and click add. Ex: "-10", then add)</span>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            fontSize: '0.95rem',
            color: '#0c4a6e'
          }}>
            <span style={{ 
              backgroundColor: '#8b5cf6', 
              color: 'white', 
              borderRadius: '50%', 
              width: '24px', 
              height: '24px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '0.875rem',
              fontWeight: 'bold'
            }}>✓</span>
            <span><strong>At end of class</strong>, Submit your totals for approval</span>
          </div>
        </div>
        <div style={{ 
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: '#e0f2fe',
          borderRadius: '0.5rem',
          border: '1px solid #0284c7'
        }}>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#0c4a6e',
            margin: 0,
            fontStyle: 'italic'
          }}>
            💡 <strong>Tip:</strong> Use the +1/-1 buttons for quick adjustments, or enter custom amounts in the "Amount" field. 
            All changes will be submitted together for admin approval at the end of class.
          </p>
        </div>
      </div>

      {/* Class Statistics */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '0.75rem', 
        padding: '1.5rem',
        marginBottom: '2rem',
        border: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        minHeight: '100px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>
            {students.length}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Total Students
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>
            {totalPP}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Total PP
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
            {averagePP}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Average PP
          </div>
        </div>
      </div>

      {/* Students List */}
      {students.length === 0 ? (
        <div style={{ 
          color: '#9ca3af', 
          textAlign: 'center', 
          padding: '3rem 2rem',
          backgroundColor: 'white',
          borderRadius: '0.75rem', 
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
          <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>
            No students in this class
          </p>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>
            Students will appear here once they're added to the classroom.
          </p>
        </div>
      ) : (
        <>
          {/* Bulk Selection Header */}
          {sortedStudents.length > 0 && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <input
                type="checkbox"
                checked={selectedStudents.size === sortedStudents.length && sortedStudents.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedStudents(new Set(sortedStudents.map(s => s.id)));
                  } else {
                    setSelectedStudents(new Set());
                  }
                }}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Select All ({selectedStudents.size} selected)
              </span>
            </div>
          )}

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {sortedStudents.map((student) => (
              <div key={student.id} style={{
                  backgroundColor: selectedStudents.has(student.id) ? '#f0f9ff' : 'white',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: selectedStudents.has(student.id) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                transition: 'box-shadow 0.2s ease'
              }}>
                {/* Checkbox for bulk selection */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedStudents.has(student.id)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedStudents);
                      if (e.target.checked) {
                        newSelected.add(student.id);
                      } else {
                        newSelected.delete(student.id);
                      }
                      setSelectedStudents(newSelected);
                    }}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                </div>

                {/* Student Info */}
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center', 
                  marginBottom: '1rem',
                  gap: '0.75rem'
                }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}>
                  {student.photoURL ? (
                    <img 
                      src={student.photoURL} 
                      alt={student.displayName}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    student.displayName.charAt(0).toUpperCase()
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <h3 style={{ 
                      fontSize: '1.1rem', 
                      fontWeight: '600', 
                      margin: 0, 
                      color: '#1f2937'
                    }}>
                      {student.displayName}
                    </h3>
                    {/* PP Boost Indicator */}
                    {activeBoosts[student.id] && (
                      <span
                        style={{
                          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                          color: 'white',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          boxShadow: '0 1px 2px rgba(251, 191, 36, 0.3)'
                        }}
                        title="Double PP Boost Active - This student will receive double PP"
                      >
                        ⚡ x2
                      </span>
                    )}
                  </div>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#6b7280', 
                    margin: 0,
                    marginBottom: '0.25rem'
                  }}>
                    {student.email}
                  </p>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#8b5cf6', 
                    margin: 0,
                    fontWeight: '500'
                  }}>
                    Level {student.level || 1} • {student.xp || 0} XP
                  </p>
                </div>
                  </div>

              {/* Power Points Display */}
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#f8fafc',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  color: '#8b5cf6',
                  marginBottom: '0.25rem'
                }}>
                  {displayPP(student)} PP
                </div>
                {!!pendingChanges[student.id] && (
                  <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                    Saved {student.powerPoints || 0} · pending {pendingChanges[student.id] > 0 ? '+' : ''}
                    {pendingChanges[student.id]}
                  </div>
                )}
              </div>

              {/* PP Controls */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                {/* Quick +/-1 Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={() => handleAdjustPP(student.id, -1)}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      padding: '0.5rem',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      minWidth: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    -
                  </button>
                <button
                    onClick={() => handleAdjustPP(student.id, 1)}
                  style={{
                      backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                      borderRadius: '0.375rem',
                    padding: '0.5rem',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                    cursor: 'pointer',
                      minWidth: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    +
                </button>
          </div>

                {/* Custom Amount Input */}
          <div style={{ 
            display: 'flex', 
                  gap: '0.5rem', 
                  alignItems: 'center'
                }}>
                  <input
                    type="number"
                    placeholder="Amount"
                    value={ppInputValues[student.id] || ''}
                    onChange={(e) => setPPInputValues(prev => ({
                      ...prev,
                      [student.id]: e.target.value ? parseInt(e.target.value) : undefined
                    }))}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      minWidth: '0' // Prevent flex item from overflowing
                    }}
                  />
            <button
                    onClick={() => {
                      const amount = ppInputValues[student.id];
                      if (amount !== undefined && amount !== 0) {
                        handleAdjustPP(student.id, amount);
                        // Clear input after use
                        setPPInputValues(prev => ({
                          ...prev,
                          [student.id]: undefined
                        }));
                      }
                    }}
                    disabled={ppInputValues[student.id] === undefined || ppInputValues[student.id] === 0}
              style={{
                      backgroundColor: (ppInputValues[student.id] !== undefined && ppInputValues[student.id] !== 0) ? '#8b5cf6' : '#9ca3af',
                      color: 'white',
                border: 'none',
                      borderRadius: '0.375rem',
                      padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: (ppInputValues[student.id] !== undefined && ppInputValues[student.id] !== 0) ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap',
                      minWidth: '60px'
                    }}
                  >
                    Add
            </button>
          </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Bulk Adjust Panel */}
      {selectedStudents.size > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          backgroundColor: '#f0f9ff',
          border: '2px solid #3b82f6',
          borderRadius: '0.75rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1e40af',
            margin: 0,
            marginBottom: '1rem'
          }}>
            Bulk Adjust PP ({selectedStudents.size} selected)
          </h3>
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                PP Amount
              </label>
              <input
                type="number"
                value={bulkPPAmount || ''}
                onChange={(e) => setBulkPPAmount(e.target.value ? parseInt(e.target.value) : 0)}
                placeholder="Enter amount (positive or negative)"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Reason (Optional)
              </label>
              <input
                type="text"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Reason for adjustment"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              />
            </div>
            <button
              onClick={handleBulkAdjustPP}
              disabled={isApplyingBulk || bulkPPAmount === 0}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: (isApplyingBulk || bulkPPAmount === 0) ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: (isApplyingBulk || bulkPPAmount === 0) ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {isApplyingBulk ? 'Applying...' : `Apply to ${selectedStudents.size} Students`}
            </button>
          </div>
        </div>
      )}

      {/* Submit Changes Button */}
      {students.length > 0 && (
      <div style={{ 
          marginTop: '2rem',
          padding: '1.5rem',
          backgroundColor: 'white',
        borderRadius: '0.75rem', 
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
      }}>
          <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ 
          fontSize: '1.125rem', 
              fontWeight: '600', 
              color: '#1f2937',
              margin: 0,
              marginBottom: '0.5rem'
            }}>
              Submit Changes for Approval
        </h3>
            <p style={{ 
          fontSize: '0.875rem', 
              color: '#6b7280',
              margin: 0
            }}>
              {pendingChangesCount > 0 
                ? `You have ${pendingChangesCount} pending change${pendingChangesCount === 1 ? '' : 's'} ready to submit.`
                : 'No changes to submit. Make some adjustments above to submit for admin approval.'
              }
            </p>
          </div>
          
          <button
            onClick={handleSubmitChanges}
            disabled={pendingChangesCount === 0 || isSubmitting}
            style={{
              backgroundColor: pendingChangesCount > 0 && !isSubmitting ? '#8b5cf6' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: pendingChangesCount > 0 && !isSubmitting ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s ease',
              minWidth: '200px'
            }}
          >
            {isSubmitting ? 'Submitting...' : `Submit ${pendingChangesCount} Change${pendingChangesCount === 1 ? '' : 's'}`}
          </button>
          
          {pendingChangesCount > 0 && (
            <div style={{ 
              marginTop: '1rem',
              fontSize: '0.75rem',
              color: '#6b7280'
            }}>
              Changes will be reviewed by an administrator before being applied.
            </div>
          )}
      </div>
      )}
    </div>
  );
};

export default ScorekeeperInterface;